import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Tooltip from "src/components/Tooltip"

import { getSpaceAssignments } from "src/api/Manage/Reservations"

import { useTracker } from "src/hooks/use_tracker"

import { formatDateShort } from "./helpers"

const SpaceAssignmentCell = ({
  reservationId,
  currentNightSpaceName,
  hasMultipleSpaces,
  night,
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const { marinaSlug } = useMarina()
  const tracker = useTracker()

  const { data: assignmentsData, isLoading: isLoadingAssignments } = useQuery(
    ["spaceAssignments", marinaSlug, reservationId],
    () => getSpaceAssignments(marinaSlug, reservationId),
    {
      enabled: showDetails && hasMultipleSpaces && Boolean(marinaSlug),
      refetchOnWindowFocus: false,
      staleTime: 300000, // 5 minutes
    }
  )

  const assignments = assignmentsData?.assignments || []
  const assignmentsUrl = `${window.location.origin}/manage/${marinaSlug}/assignments_v2?night=${night}&reservation_id=${reservationId}`

  const renderAssignmentDetails = () => {
    if (isLoadingAssignments) {
      return <div className="p-2 text-sm">Loading...</div>
    }

    if (assignments.length === 0) {
      return <div className="p-2 text-sm">No assignment details available</div>
    }

    return (
      <div className="max-w-sm space-y-1 p-2 text-sm">
        {assignments.map((assignment, index) => (
          <div key={index} className="flex justify-between whitespace-nowrap">
            <strong>{assignment.space_name}:</strong>
            <span className="ml-2">
              {assignment.start_date === assignment.end_date
                ? formatDateShort(assignment.start_date)
                : `${formatDateShort(
                    assignment.start_date
                  )} - ${formatDateShort(assignment.end_date)}`}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (!currentNightSpaceName) {
    return (
      <a
        href={assignmentsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-secondary btn-sm"
        onClick={(e) => {
          e.stopPropagation()
          tracker.trackEvent("tonights_boats:space_assignment_clicked", {
            action: "add",
          })
        }}
      >
        Add
      </a>
    )
  }

  if (hasMultipleSpaces) {
    return (
      <div className="inline-flex items-center gap-1">
        <a
          href={assignmentsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
          onClick={(e) => {
            e.stopPropagation()
            tracker.trackEvent("tonights_boats:space_assignment_clicked", {
              action: "view",
              has_multiple_spaces: true,
            })
          }}
        >
          {currentNightSpaceName}
        </a>
        <Tooltip text={renderAssignmentDetails()} placement="right">
          <div
            className="cursor-help whitespace-nowrap text-xs text-gray-500"
            onMouseEnter={() => setShowDetails(true)}
            onMouseLeave={() => setShowDetails(false)}
            onClick={(e) => e.stopPropagation()}
          >
            (+more)
          </div>
        </Tooltip>
      </div>
    )
  }

  return (
    <a
      href={assignmentsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800"
      onClick={(e) => {
        e.stopPropagation()
        tracker.trackEvent("tonights_boats:space_assignment_clicked", {
          action: "view",
          has_multiple_spaces: false,
        })
      }}
    >
      {currentNightSpaceName}
    </a>
  )
}

SpaceAssignmentCell.propTypes = {
  reservationId: PropTypes.string.isRequired,
  currentNightSpaceName: PropTypes.string,
  hasMultipleSpaces: PropTypes.bool,
  night: PropTypes.string.isRequired,
}

export default SpaceAssignmentCell
